import React, { useEffect, useMemo, useState } from 'react';
import { Card, Modal, Table, Select, Typography, Button, message, Space, Popconfirm, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import type { MaterialShelf } from '../../interfaces/MaterialInventory.interface';
import {
	useGetInventoryLocationsQuery,
	useUpdateMaterialLengthMutation,
} from '../../redux/api/material';
import { useGetBuildingsQuery } from '../../redux/api/location';
import {
	toNum,
	getAvailableColumnLabel,
	hasNoAvailability,
	barFitsInLocation,
	getMaterialInfoFromRecord,
	getDimensionValue,
	renderLocationOption,
	filterLocationOption,
} from './materialLengthLocationUtils';
import BuildingTag from '../BuildingTag';
import OperatorPinModal from '../Operators/OperatorPinModal';
import { locationSearchText, useLocationLabel } from '../../utils/locationLabel';
import useIsShopManager from '../../hooks/useIsShopManager';
import { lengthHasPartAssignment } from './materialLengthAssignmentUtils';

export interface MoveMaterialSummaryData {
	materialInfo: {
		type: string;
		width?: number;
		height?: number;
		shape?: string;
		outer_diameter?: number;
		inner_diameter?: number;
		wall_thickness?: number;
	};
	lengths: Array<{
		id: string;
		friendlyId: string;
		location: string;
		length: number;
		isScrapped?: boolean;
	}>;
}

export interface MoveMaterialModalProps {
	open: boolean;
	onClose: () => void;
	selectedLengths: any[];
	shelves: MaterialShelf[] | undefined;
	onConfirmSuccess?: (summaryData: MoveMaterialSummaryData) => void;
	refetch?: () => void;
	isTablet?: boolean;
	/** When provided, each row gets a "Remove from list" button so users can drop accidentally-selected rows without leaving the modal. */
	onRemoveLength?: (id: string) => void;
}

/**
 * Modal for moving one or more material lengths to a different location.
 * User picks destination (building + location), Confirm, then identifies via PIN
 * in a separate step before the save and summary.
 *
 * TODO: Material shelves (shelves prop / useGetMaterialShelvesQuery) still require verification
 * with the code implemented for 1:1 move (e.g. inventory location vs shelf semantics and API).
 */
const MoveMaterialModal: React.FC<MoveMaterialModalProps> = ({
	open,
	onClose,
	selectedLengths,
	onConfirmSuccess,
	refetch: refetchList,
	isTablet = false,
	onRemoveLength,
}) => {
	const [updateMaterialLength] = useUpdateMaterialLengthMutation();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { isShopManager } = useIsShopManager();
	const { data: buildings = [] } = useGetBuildingsQuery(undefined, { skip: !open });
	const sourceBuildingId = useMemo(() => {
		const first = selectedLengths[0];
		return first?.inventory_location?.shelf?.rack?.building?.id ?? undefined;
	}, [selectedLengths]);
	const sourceBuildingName = useMemo(() => {
		const first = selectedLengths[0];
		return first?.inventory_location?.shelf?.rack?.building?.name ?? null;
	}, [selectedLengths]);
	const allUnassignedToPart = useMemo(
		() =>
			selectedLengths.length > 0 &&
			selectedLengths.every((len: any) => !lengthHasPartAssignment(len)),
		[selectedLengths]
	);
	/** PTB-7313: shop managers may move unassigned stock across buildings. */
	const canChangeBuilding =
		isShopManager === true && allUnassignedToPart && buildings.length > 1;
	const [targetBuildingId, setTargetBuildingId] = useState<string | undefined>(
		undefined
	);
	const buildingId = targetBuildingId ?? sourceBuildingId;
	const buildingName = useMemo(() => {
		if (buildingId) {
			const match = buildings.find((b: { id: string; name: string }) => String(b.id) === String(buildingId));
			if (match?.name) return match.name;
		}
		return sourceBuildingName;
	}, [buildingId, buildings, sourceBuildingName]);
	const isCrossBuildingMove =
		Boolean(sourceBuildingId) &&
		Boolean(buildingId) &&
		String(sourceBuildingId) !== String(buildingId);
	const formatLocation = useLocationLabel();
	const locLabel = (loc: any, fallback: string) =>
		formatLocation(loc?.display_name) || loc?.code || fallback;
	// Per-length selected location (shelf id). Defaults to current location.
	const [locationByLengthId, setLocationByLengthId] = useState<
		Record<string, string>
	>({});
	const [showErrors, setShowErrors] = useState(false);
	/** After Confirm on locations, ask for PIN before saving. */
	const [operatorPinOpen, setOperatorPinOpen] = useState(false);

	// Candidate locations per material length (GET inventory/locations/?material_length_ids=... returns by_material_length)
	const materialLengthIds = useMemo(
		() => selectedLengths.map((len: any) => String(len.id)),
		[selectedLengths]
	);
	const { data: locationsData, isLoading: locationsLoading } =
		useGetInventoryLocationsQuery(
			{ material_length_ids: materialLengthIds, ...(buildingId && { building: buildingId }) },
			{
				skip: !open || materialLengthIds.length === 0,
				refetchOnMountOrArgChange: true,
			}
		);

	// Per–material-length consumption from API (weight, volume, width, height per bar)
	const consumptionByLengthId = useMemo(() => {
		const out: Record<string, { weight_per_bar: number; volume_per_bar: number; width_per_bar: number; height_per_bar: number }> = {};
		(locationsData?.by_material_length ?? []).forEach((entry: any) => {
			const mid = entry.material_length_id != null ? String(entry.material_length_id) : '';
			const c = entry.consumption;
			if (!c) {
				out[mid] = { weight_per_bar: 0, volume_per_bar: 0, width_per_bar: 0, height_per_bar: 0 };
				return;
			}
			out[mid] = {
				weight_per_bar: toNum(c.weight_per_bar),
				volume_per_bar: toNum(c.volume_per_bar),
				width_per_bar: toNum(c.width_per_bar),
				height_per_bar: toNum(c.height_per_bar),
			};
		});
		return out;
	}, [locationsData?.by_material_length]);

	// Valid location IDs per length from API only (for initial selection effect — no dependency on locationByLengthId to avoid update loop)
	const initialValidOptionIdsByLengthId = useMemo(() => {
		const out: Record<string, Set<string>> = {};
		const byMaterialLength = locationsData?.by_material_length ?? [];
		byMaterialLength.forEach((entry: any) => {
			const mid = entry.material_length_id != null ? String(entry.material_length_id) : '';
			const locs = entry.locations ?? [];
			const set = new Set<string>();
			locs.forEach((loc: any) => {
				if (loc) set.add(String(loc.id));
			});
			const len = selectedLengths.find((l: any) => String(l.id) === mid);
			if (len?.inventory_location != null && typeof len.inventory_location === 'object') {
				const loc = len.inventory_location;
				const currentId = loc.id ?? loc;
				if (currentId) set.add(String(currentId));
			}
			out[mid] = set;
		});
		selectedLengths.forEach((len: any) => {
			const mid = String(len.id);
			if (!out[mid]) out[mid] = new Set();
		});
		return out;
	}, [locationsData?.by_material_length, selectedLengths]);

	// Consumed capacity per location (sum of consumption of all lengths currently assigned there in this session)
	// For INDIVIDUAL_SLOTS the limiting metric is `bars` (each assigned length consumes one slot).
	const consumedPerLocation = useMemo(() => {
		const consumed: Record<string, { weight: number; volume: number; width: number; height: number; bars: number }> = {};
		selectedLengths.forEach((len: any) => {
			const locId = locationByLengthId[String(len.id)];
			if (!locId) return;
			const key = String(locId);
			const c = consumptionByLengthId[String(len.id)];
			if (!c) return;
			if (!consumed[key]) consumed[key] = { weight: 0, volume: 0, width: 0, height: 0, bars: 0 };
			consumed[key].weight += c.weight_per_bar;
			consumed[key].volume += c.volume_per_bar;
			consumed[key].width += c.width_per_bar;
			consumed[key].height += c.height_per_bar;
			consumed[key].bars += 1;
		});
		return consumed;
	}, [selectedLengths, locationByLengthId, consumptionByLengthId]);

	type LocationOption = {
		value: string;
		label: string;
		disabled?: boolean;
		isCurrent?: boolean;
		searchText?: string;
	};

	// Per–material-length options: candidate locations (disabled when no availability or doesn't fit)
	const optionsByLengthId = useMemo((): Record<string, LocationOption[]> => {
		const byLengthId: Record<string, LocationOption[]> = {};
		selectedLengths.forEach((len: any) => {
			byLengthId[String(len.id)] = [];
		});
		const byMaterialLength = locationsData?.by_material_length ?? [];
		const locIdToLabel = new Map<string, string>();
		const locIdToType = new Map<string, string>();
		byMaterialLength.forEach((entry: any) => {
			(entry.locations ?? []).forEach((loc: any) => {
				if (loc) {
					const id = String(loc.id);
					if (!locIdToLabel.has(id)) locIdToLabel.set(id, locLabel(loc, id));
					if (!locIdToType.has(id)) locIdToType.set(id, loc.type ?? '');
				}
			});
		});
		selectedLengths.forEach((len: any) => {
			const loc = len.inventory_location;
			if (loc != null && typeof loc === 'object') {
				const id = String(loc.id ?? loc);
				if (id && !locIdToLabel.has(id)) locIdToLabel.set(id, locLabel(loc, id));
				if (id && !locIdToType.has(id)) locIdToType.set(id, (loc as { type?: string }).type ?? '');
			}
		});
		byMaterialLength.forEach((entry: any) => {
			const mid = entry.material_length_id != null ? String(entry.material_length_id) : '';
			const consumption = consumptionByLengthId[mid];
			const locs = entry.locations ?? [];
			const lenConsumption = consumption ?? { weight_per_bar: 0, volume_per_bar: 0, width_per_bar: 0, height_per_bar: 0 };
			const options: LocationOption[] = [];
			locs.forEach((loc: any) => {
				if (loc == null) return;
				const locId = String(loc.id);
				const consumed = consumedPerLocation[locId];
				const cw = consumed?.weight ?? 0;
				const cv = consumed?.volume ?? 0;
				const cwd = consumed?.width ?? 0;
				const ch = consumed?.height ?? 0;
				const cBars = consumed?.bars ?? 0;
				const baseAvailW = toNum(loc.available_weight);
				const baseAvailV = toNum(loc.available_volume);
				const baseAvailWd = toNum(loc.available_width);
				const baseAvailH = toNum(loc.available_height);
				const baseAvailBars = toNum(loc.available_bars);
				const isCurrentBarAssignedHere = locationByLengthId[String(mid)] === locId;
				const addBack = isCurrentBarAssignedHere ? lenConsumption : { weight_per_bar: 0, volume_per_bar: 0, width_per_bar: 0, height_per_bar: 0 };
				const addBackBars = isCurrentBarAssignedHere ? 1 : 0;
				const effectiveAvailable = {
					weight: Math.max(0, baseAvailW - cw + addBack.weight_per_bar),
					volume: Math.max(0, baseAvailV - cv + addBack.volume_per_bar),
					width: Math.max(0, baseAvailWd - cwd + addBack.width_per_bar),
					height: Math.max(0, baseAvailH - ch + addBack.height_per_bar),
					bars: Math.max(0, baseAvailBars - cBars + addBackBars),
				};
			const fits = barFitsInLocation(loc, lenConsumption, effectiveAvailable);
			const noAvailability = hasNoAvailability(loc.type ?? '', effectiveAvailable);
				options.push({
					value: locId,
					label: locLabel(loc, locId),
					searchText: locationSearchText(loc?.display_name, formatLocation) || undefined,
					disabled: !fits || noAvailability,
					isCurrent: Boolean(loc.is_current_location),
				});
			});
			// Ensure currently selected location is always in the list (so Select value matches an option and selection sticks)
			const selectedLocId = locationByLengthId[String(mid)];
			if (selectedLocId && !options.some((o) => o.value === selectedLocId)) {
				const displayName = locIdToLabel.get(selectedLocId) ?? selectedLocId;
				options.push({ value: selectedLocId, label: displayName, disabled: false });
			}
			// Fallback: include the current location when the backend didn't flag it
			// (it normally returns it with is_current_location). Skip on cross-building
			// moves — the source slot is not a destination in the other building.
			const len = selectedLengths.find((l: any) => String(l.id) === mid);
			if (
				!isCrossBuildingMove &&
				len?.inventory_location != null &&
				typeof len.inventory_location === 'object'
			) {
				const loc = len.inventory_location;
				const currentId = String(loc.id ?? loc);
				const label = formatLocation(loc.display_name) || loc.code;
				if (currentId && label && !options.some((o) => o.value === currentId)) {
					options.push({
						value: currentId,
						label: label,
						searchText: locationSearchText(loc.display_name, formatLocation) || undefined,
						disabled: false,
						isCurrent: true,
					});
				}
			}
			// Deduplicate by location id (same location only once per dropdown; keep first = correct effective availability for this row)
			const seen = new Set<string>();
			byLengthId[mid] = options.filter((o) => {
				if (seen.has(o.value)) return false;
				seen.add(o.value);
				return true;
			});
		});
		return byLengthId;
	}, [locationsData?.by_material_length, selectedLengths, locationByLengthId, consumptionByLengthId, consumedPerLocation, formatLocation, isCrossBuildingMove]);

	// Per–material-length set of all option values (for Select value display)
	const optionValueSetByLengthId = useMemo(() => {
		const out: Record<string, Set<string>> = {};
		Object.keys(optionsByLengthId).forEach((mid) => {
			out[mid] = new Set(optionsByLengthId[mid].map((o) => o.value));
		});
		return out;
	}, [optionsByLengthId]);

	// Per-material-length set of enabled option values only (disabled = no availability)
	const enabledOptionValueSetByLengthId = useMemo(() => {
		const out: Record<string, Set<string>> = {};
		Object.keys(optionsByLengthId).forEach((mid) => {
			out[mid] = new Set(
				optionsByLengthId[mid].filter((o) => !o.disabled).map((o) => o.value)
			);
		});
		return out;
	}, [optionsByLengthId]);

	// Scenario 4: Operator selection (Machine Operators group), prefilled
	// Reset destination building to the bar's current building when the modal opens.
	useEffect(() => {
		if (!open) {
			setTargetBuildingId(undefined);
			setOperatorPinOpen(false);
			return;
		}
		setTargetBuildingId(sourceBuildingId);
	}, [open, sourceBuildingId]);

	// Set initial selection when modal opens (use API-based valid IDs only to avoid dependency on locationByLengthId → options → effect loop)
	useEffect(() => {
		if (!open || !selectedLengths.length) {
			setLocationByLengthId({});
			setShowErrors(false);
			return;
		}
		const initial: Record<string, string> = {};
		// Cross-building: do not preselect the source location (it is not a candidate).
		if (!isCrossBuildingMove) {
			selectedLengths.forEach((len: any) => {
				const mid = String(len.id);
				const validIds = initialValidOptionIdsByLengthId[mid];
				if (!validIds) return;
				const locId =
					len.inventory_location?.id ??
					len.inventory_location ??
					len.material_shelf?.id ??
					len.material_shelf;
				const key = locId != null && locId !== '' ? String(locId) : null;
				if (key && validIds.has(key)) initial[mid] = key;
			});
		}
		setLocationByLengthId(initial);
		setShowErrors(false);
	}, [open, selectedLengths, initialValidOptionIdsByLengthId, isCrossBuildingMove]);

	const handleBuildingChange = (nextBuildingId: string) => {
		setTargetBuildingId(nextBuildingId);
		setLocationByLengthId({});
		setShowErrors(false);
	};

	const handleLocationChange = (lengthId: string, locationId: string) => {
		setLocationByLengthId((prev) => ({
			...prev,
			[String(lengthId)]: String(locationId),
		}));
	};

	// Map of location id -> display name for summary (from all per-item locations + current with label)
	const locationDisplayById = useMemo(() => {
		const map = new Map<string, string>();
		(locationsData?.by_material_length ?? []).forEach((entry: any) => {
			(entry.locations ?? []).forEach((loc: any) => {
				if (loc == null) return;
				map.set(String(loc.id), formatLocation(loc.display_name) || loc.code || loc.id);
			});
		});
		selectedLengths.forEach((len: any) => {
			const loc = len.inventory_location;
			if (loc == null || typeof loc !== 'object') return;
			const id = loc.id ?? loc;
			if (!id || map.has(String(id))) return;
			const label = formatLocation(loc.display_name) || loc.code;
			if (label) map.set(String(id), label);
		});
		return map;
	}, [locationsData?.by_material_length, selectedLengths, formatLocation]);

	// Location id -> { type, available_* } for confirm-time capacity validation (from first occurrence in API response)
	const locationMetaById = useMemo(() => {
		const map = new Map<string, { type: string; available_weight: number; available_volume: number; available_width: number; available_height: number; available_bars: number }>();
		(locationsData?.by_material_length ?? []).forEach((entry: any) => {
			(entry.locations ?? []).forEach((loc: any) => {
				if (loc == null || map.has(String(loc.id))) return;
				map.set(String(loc.id), {
					type: loc.type ?? '',
					available_weight: toNum(loc.available_weight),
					available_volume: toNum(loc.available_volume),
					available_width: toNum(loc.available_width),
					available_height: toNum(loc.available_height),
					available_bars: toNum(loc.available_bars),
				});
			});
		});
		return map;
	}, [locationsData?.by_material_length]);

	// Per-row "Available" column: same value for every row that has the same location — "this location has X lbs left" (true remaining after all bars assigned there)
	const availableLabelByLengthId = useMemo(() => {
		const out: Record<string, string> = {};
		selectedLengths.forEach((len: any) => {
			const mid = String(len.id);
			const selectedLocId = locationByLengthId[mid];
			if (!selectedLocId) {
				out[mid] = '—';
				return;
			}
			const meta = locationMetaById.get(selectedLocId);
			const consumed = consumedPerLocation[selectedLocId];
			const cw = consumed?.weight ?? 0;
			const cv = consumed?.volume ?? 0;
			const cwd = consumed?.width ?? 0;
			const ch = consumed?.height ?? 0;
			const cBars = consumed?.bars ?? 0;
			// No add-back: show true remaining at this location (same for every row that selected it)
			const effectiveAvailable = {
				weight: Math.max(0, (meta?.available_weight ?? 0) - cw),
				volume: Math.max(0, (meta?.available_volume ?? 0) - cv),
				width: Math.max(0, (meta?.available_width ?? 0) - cwd),
				height: Math.max(0, (meta?.available_height ?? 0) - ch),
				bars: Math.max(0, (meta?.available_bars ?? 0) - cBars),
			};
			const locType = meta?.type ?? '';
			out[mid] = getAvailableColumnLabel(locType, effectiveAvailable);
		});
		return out;
	}, [selectedLengths, locationByLengthId, locationMetaById, consumedPerLocation]);

	const getLocationError = (record: any) => {
		const mid = String(record.id);
		const locationId = locationByLengthId[mid];
		const enabledSet = enabledOptionValueSetByLengthId[mid];

		return !locationId || !enabledSet?.has(String(locationId))
			? 'Select a valid location.'
			: undefined;
	};

	const renderInlineError = (error?: string) =>
		showErrors && error ? (
			<Typography.Text type="danger" style={{ display: 'block', marginTop: 4, textAlign: 'left' }}>
				{error}
			</Typography.Text>
		) : null;

	const hasMissingLocations = () =>
		selectedLengths.some((record: any) => Boolean(getLocationError(record)));

	// At confirm time: every assigned location must have consumed <= available for its limiting dimension
	function validateAssignmentsFit(): boolean {
		for (const locId of Object.keys(consumedPerLocation)) {
			const consumed = consumedPerLocation[locId];
			const meta = locationMetaById.get(locId);
			if (!meta) continue; // e.g. current location not in candidates; no capacity limit
			const { type } = meta;
			if (type === 'SHARED_SHELVES' && meta.available_weight > 0) {
				if (consumed.weight > meta.available_weight) return false;
			} else if (type === 'SHARED_BINS' && meta.available_volume > 0) {
				if (consumed.volume > meta.available_volume) return false;
			} else if (type === 'INDIVIDUAL_SLOTS') {
				if (meta.available_bars > 0 && consumed.bars > meta.available_bars) return false;
			}
		}
		return true;
	}

	const buildSummaryData = (
		locationIdByLengthId: Record<string, string>
	): MoveMaterialSummaryData => {
		const first = selectedLengths[0];
		return {
			materialInfo: getMaterialInfoFromRecord(first),
			lengths: selectedLengths.map((record: any) => {
				const locationId =
					locationIdByLengthId[String(record.id)] ??
					record.inventory_location?.id ??
					record.inventory_location ??
					record.material_shelf?.id ??
					record.material_shelf;
				const locationName = locationId
					? locationDisplayById.get(String(locationId)) ?? 'Unknown'
					: 'Unknown';
				return {
					id: String(record.id),
					friendlyId: record.friendly_id ?? '',
					location: locationName,
					length: Number(record.current_length) || 0,
					isScrapped: record.status === 'Scrapped',
					materialInfo: getMaterialInfoFromRecord(record),
				};
			}),
		};
	};

	/** Step 1: validate destinations, then ask for operator PIN. */
	const handleConfirm = () => {
		if (hasMissingLocations()) {
			setShowErrors(true);
			return;
		}
		if (!selectedLengths.length) return;
		if (!validateAssignmentsFit()) {
			message.error('One or more selected locations would exceed capacity. Please choose different locations.');
			return;
		}
		setOperatorPinOpen(true);
	};

	/** Step 2: PIN resolved — save locations and hand off to summary. */
	const handleOperatorConfirm = async (_userId: number, _pin: string) => {
		if (isSubmitting) return;
		const locationIdByLengthId = locationByLengthId;
		setIsSubmitting(true);
		try {
			await Promise.all(
				selectedLengths.map((len: any) => {
					const locationId =
						locationIdByLengthId[String(len.id)] ??
						len.inventory_location?.id ??
						len.inventory_location ??
						len.material_shelf?.id ??
						len.material_shelf;
					return updateMaterialLength({
						id: String(len.id),
						values: { inventory_location: String(locationId) },
					}).unwrap();
				})
			);
			refetchList?.();
			const summaryData = buildSummaryData(locationIdByLengthId);
			setOperatorPinOpen(false);
			onClose();
			onConfirmSuccess?.(summaryData);
		} catch (err: any) {
			console.error('Failed to move material lengths:', err);
			const detail = err?.data?.detail;
			message.error(
				typeof detail === 'string'
					? detail
					: 'Failed to save location changes. Please try again.'
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const columns = [
		...(onRemoveLength
			? [
				{
					title: '',
					key: 'remove',
					width: 44,
					fixed: 'left' as const,
					render: (_: unknown, record: any) => (
						<Popconfirm
							title="Remove from list?"
							description="The selected location for this row will be discarded."
							okText="Remove"
							okButtonProps={{ danger: true }}
							onConfirm={() => onRemoveLength(String(record.id))}
						>
							<Tooltip title="Remove from list">
								<Button
									type="text"
									danger
									size="small"
									icon={<CloseOutlined />}
									aria-label="Remove from list"
								/>
							</Tooltip>
						</Popconfirm>
					),
				},
			]
			: []),
		{
			title: 'Material Type',
			dataIndex: ['material_type', 'display_name'],
			key: 'material_type',
			width: 160,
			render: (_: unknown, record: any) =>
				record.material_type?.display_name ?? '-',
		},
		{
			title: 'Width',
			key: 'width',
			width: 100,
			render: (_: unknown, record: any) => {
				const val = getDimensionValue(record, 'width_id');
				return val != null ? Number(val).toFixed(3) : '-';
			},
		},
		{
			title: 'Height',
			key: 'height',
			width: 100,
			render: (_: unknown, record: any) => {
				const val = getDimensionValue(record, 'height_od');
				return val != null ? Number(val).toFixed(3) : '-';
			},
		},
		{
			title: 'ID',
			dataIndex: 'friendly_id',
			key: 'friendly_id',
			width: 100,
		},
		{
			title: 'Length',
			dataIndex: 'current_length',
			key: 'current_length',
			width: 100,
			render: (val: number) =>
				val != null ? `${Number(val).toFixed(1)} in.` : '-',
		},
		{
			title: (
				<>
					Location <Typography.Text type="danger">*</Typography.Text>
				</>
			),
			key: 'location',
			width: 220,
			render: (_: unknown, record: any) => {
				const mid = String(record.id);
				const rowOptions = optionsByLengthId[mid] ?? [];
				const rowOptionSet = optionValueSetByLengthId[mid];
				const currentLocId =
					record.inventory_location?.id ??
					record.inventory_location ??
					record.material_shelf?.id ??
					record.material_shelf;
				const rawValue =
					locationByLengthId[mid] ??
					(currentLocId != null && currentLocId !== ''
						? String(currentLocId)
						: undefined);
				const rawStr = rawValue != null ? String(rawValue) : undefined;
				// Only show a chosen value when it exists in this row's options (avoid showing UUID or synthetic label)
				const value =
					rawStr && rowOptionSet?.has(rawStr) ? rawStr : undefined;
				const error = getLocationError(record);
				return (
					<>
						<Select
							placeholder={
								locationsLoading
									? 'Loading locations...'
									: 'Select location'
							}
							value={value}
							onChange={(locationId) =>
								handleLocationChange(mid, locationId)
							}
							options={rowOptions}
							optionRender={(opt) => renderLocationOption(opt.data as LocationOption)}
							style={{ width: '100%' }}
							showSearch
							filterOption={filterLocationOption}
							allowClear={false}
							loading={locationsLoading}
						/>
						{renderInlineError(error)}
					</>
				);
			},
		},
		{
			title: 'Available',
			key: 'available',
			width: 140,
			render: (_: unknown, record: any) => {
				const mid = String(record.id);
				return availableLabelByLengthId[mid] ?? '—';
			},
		},
	];

	const renderTabletCards = () => (
		<Space direction="vertical" size="middle" style={{ width: '100%' }}>
			{selectedLengths.map((record: any) => {
				const mid = String(record.id);
				const rowOptions = optionsByLengthId[mid] ?? [];
				const rowOptionSet = optionValueSetByLengthId[mid];
				const currentLocId =
					record.inventory_location?.id ??
					record.inventory_location ??
					record.material_shelf?.id ??
					record.material_shelf;
				const rawValue =
					locationByLengthId[mid] ??
					(currentLocId != null && currentLocId !== ''
						? String(currentLocId)
						: undefined);
				const value =
					rawValue != null && rowOptionSet?.has(String(rawValue))
						? String(rawValue)
						: undefined;
				const locationError = getLocationError(record);
				const renderReadOnlyField = (label: string, value: React.ReactNode) => (
					<div>
						<Typography.Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
							{label}
						</Typography.Text>
						<div
							style={{
								minHeight: 40,
								display: 'flex',
								alignItems: 'center',
								whiteSpace: 'nowrap',
							}}
						>
							{value}
						</div>
					</div>
				);

				return (
					<Card
						key={record.id}
						size="small"
						title={`${record.friendly_id ?? 'Material Length'} - ${
							record.material_type?.display_name ?? 'Material'
						}`}
						styles={{ body: { padding: 12 } }}
					>
						{onRemoveLength && (
							<div
								style={{
									display: 'flex',
									justifyContent: 'flex-end',
									marginBottom: 12,
								}}
							>
								<Popconfirm
									title="Remove from list?"
									description="The selected location for this row will be discarded."
									okText="Remove"
									okButtonProps={{ danger: true }}
									onConfirm={() => onRemoveLength(String(record.id))}
								>
									<Button
										danger
										size="middle"
										icon={<CloseOutlined />}
										aria-label="Remove from list"
									>
										Remove from list
									</Button>
								</Popconfirm>
							</div>
						)}
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: '1.4fr repeat(4, minmax(80px, 1fr)) 320px',
								gap: 12,
								alignItems: 'start',
								overflowX: 'auto',
							}}
						>
							{renderReadOnlyField(
								'Material Type',
								record.material_type?.display_name ?? '-'
							)}
							{renderReadOnlyField(
								'Width',
								getDimensionValue(record, 'width_id') != null
									? Number(getDimensionValue(record, 'width_id')).toFixed(3)
									: '-'
							)}
							{renderReadOnlyField(
								'Height',
								getDimensionValue(record, 'height_od') != null
									? Number(getDimensionValue(record, 'height_od')).toFixed(3)
									: '-'
							)}
							{renderReadOnlyField('ID', record.friendly_id ?? '-')}
							{renderReadOnlyField(
								'Length',
								`${Number(record.current_length || 0).toFixed(1)} in.`
							)}
							<div>
								<Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
									Location <Typography.Text type="danger">*</Typography.Text>
								</Typography.Text>
								<Select
									placeholder={locationsLoading ? 'Loading locations...' : 'Select location'}
									value={value}
									onChange={(locationId) => handleLocationChange(mid, locationId)}
									options={rowOptions}
									optionRender={(opt) => renderLocationOption(opt.data as LocationOption)}
									style={{ width: '100%' }}
									size="large"
									showSearch
									filterOption={filterLocationOption}
									allowClear={false}
									loading={locationsLoading}
								/>
								{renderInlineError(locationError)}
								<Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
									Available: {availableLabelByLengthId[mid] ?? '—'}
								</Typography.Text>
							</div>
						</div>
					</Card>
				);
			})}
		</Space>
	);

	const buildingField = canChangeBuilding ? (
		<div style={{ width: isTablet ? 300 : 360, textAlign: 'left' }}>
			<Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
				Building
			</Typography.Text>
			<Select
				placeholder="Select building"
				value={buildingId ? String(buildingId) : undefined}
				onChange={handleBuildingChange}
				options={buildings.map((b: { id: string; name: string }) => ({
					value: String(b.id),
					label: b.name,
				}))}
				style={{ width: '100%' }}
				showSearch
				optionFilterProp="label"
			/>
			{isCrossBuildingMove && (
				<Typography.Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
					Moving from {sourceBuildingName ?? 'current building'} to {buildingName}.
				</Typography.Text>
			)}
		</div>
	) : null;

	return (
		<>
			<Modal
				title={
					<Space>
						<span>Move Material</span>
						<BuildingTag name={buildingName} />
					</Space>
				}
				open={open}
				onCancel={() => {
					setOperatorPinOpen(false);
					onClose();
				}}
				maskClosable={false}
				footer={
					selectedLengths.length > 0 ? (
					<Space wrap align="center" size={isTablet ? 'middle' : 'small'}>
						<Button size={isTablet ? 'large' : 'middle'} onClick={onClose}>Cancel</Button>
						<Button
							type="primary"
							size={isTablet ? 'large' : 'middle'}
							onClick={handleConfirm}
							loading={isSubmitting}
							disabled={isSubmitting}
						>
							Confirm
						</Button>
					</Space>
					) : null
				}
				width={isTablet ? '92vw' : 960}
				centered
				destroyOnClose
				styles={{ body: { maxHeight: isTablet ? '70vh' : undefined, overflowY: isTablet ? 'auto' : undefined } }}
			>
				{selectedLengths.length === 0 ? (
					<Typography.Text type="secondary">
						No material lengths selected.
					</Typography.Text>
				) : (
					<Space direction="vertical" size="middle" style={{ width: '100%' }}>
						{buildingField && <div style={{ marginBottom: 8 }}>{buildingField}</div>}
						{isTablet ? (
							renderTabletCards()
						) : (
							<Table
								columns={columns}
								dataSource={selectedLengths}
								rowKey="id"
								pagination={false}
								scroll={{ y: 400 }}
								size="small"
							/>
						)}
					</Space>
				)}
			</Modal>

			<OperatorPinModal
				open={open && operatorPinOpen}
				title="Identify operator"
				description="Enter the operator PIN to confirm this material move."
				label="Operator"
				onCancel={() => setOperatorPinOpen(false)}
				onConfirm={handleOperatorConfirm}
			/>
		</>
	);
};

export default MoveMaterialModal;
